import { parseToRgba, toHex } from 'color2k';

/**
 * Blends a transparent color with a background color, removing the transparency and
 * returning the resulting solid color.
 *
 * This function simulates the effect of applying a transparent color over a solid
 * background, then "flattening" the result to remove the transparency.
 *
 * @param {string} background - The background color in hex format (e.g., "#ffffff").
 * @param {string} overlay - The overlay color in hex format with alpha (e.g., "#009de0e0").
 * @returns {string} The resulting solid color in hex format.
 *
 * @example
 * const lightModeResult = blendColorWithBackground('#ffffff', '#009de0e0');
 * console.log(lightModeResult); // "#1fa9e4"
 *
 * const darkModeResult = blendColorWithBackground('#000000', '#009de0e0');
 * console.log(darkModeResult); // "#008ac5"
 */
export function blendColorWithBackground(background: string, overlay: string): string {
  // Not sure why, but the seemingly redundant toHex conversion is required to get the correct result
  const bgRgba = parseToRgba(toHex(background));

  if (bgRgba[3] !== 1) {
    throw new Error('blendColorWithBackground: background color must be opaque');
  }

  const overlayRgba = parseToRgba(toHex(overlay));
  const alpha = overlayRgba[3];

  const r = Math.round((1 - alpha) * bgRgba[0] + alpha * overlayRgba[0]);
  const g = Math.round((1 - alpha) * bgRgba[1] + alpha * overlayRgba[1]);
  const b = Math.round((1 - alpha) * bgRgba[2] + alpha * overlayRgba[2]);

  return toHex(`rgb(${r}, ${g}, ${b})`);
}
